/**
 * @file index.tsx
 *
 * @fileoverview Displays a google places API Autocomplete component.
 */

import React, { useState } from "react";
import Geosuggest, {
  Props as GeosuggestProps,
} from "@ubilabs/react-geosuggest";
import HiddenVisually from "../../../14-w-a11y/02-visually-hidden";
import styles from "./googleGeocomplete.module.scss";
import { VariantProps, cva } from "class-variance-authority";

const inputIcon = cva([styles.inputIcon], {
  variants: {
    iconPosition: {
      start: styles.iconStart,
      end: styles.iconEnd,
    },
  },
  defaultVariants: {
    iconPosition: "start",
  },
});

const autocompleteInputWrapper = cva([styles.googleAutocompleteInputWrapper], {
  variants: {
    iconPosition: {
      start: styles.inputIconStart,
      end: styles.inputIconEnd,
    },
  },
});

export interface GoogleGEOAutocompleteProps
  extends GeosuggestProps,
    VariantProps<typeof inputIcon> {
  /**
   * Input size.
   *
   * @default "40"
   */
  size?: number;
  /**
   * Elements to display on focus.
   */
  focusElements?: React.ReactNode;
  /**
   * Ref for the component.
   */
  refElement?: any;
  /**
   * id for the autocomplete.
   *
   * @default "geoautosuggest"
   */
  id?: string;
  /**
   * Label for the autocomplete.
   *
   * @default "Geo autocomplete"
   */
  label?: string;
  /**
   * The input icon.
   */
  Icon?: React.ReactNode;
  /**
   * Disable the suggests dropdown.
   *
   * @default false
   */
  disableSuggests?: boolean;
}

export const GoogleGEOAutocomplete = ({
  refElement,
  focusElements = null,
  id = "geoautosuggest",
  label = "Geo autocomplete",
  Icon = null,
  iconPosition = "start",
  size = 40,
  disableSuggests = false,
  ...props
}: GoogleGEOAutocompleteProps) => {
  const [showFocusElements, setShowFocusElements] = useState(false);

  return (
    <div className={styles.googleAutocompleteWrapper}>
      {label && (
        <HiddenVisually>
          <label htmlFor={`geosuggest__input--${id}`}>{label}</label>
        </HiddenVisually>
      )}
      <div
        className={autocompleteInputWrapper({
          iconPosition: Icon ? iconPosition : null,
        })}
      >
        <Geosuggest
          {...(refElement ? { ref: refElement } : null)}
          id={id}
          autoComplete="off"
          // @ts-ignore
          size={size}
          {...props}
          {...(disableSuggests
            ? { skipSuggest: () => true, minLength: 9999 }
            : null)}
          {...(focusElements
            ? {
                onFocus: () => {
                  setShowFocusElements(true);
                },
                onBlur: () => {
                  setTimeout(() => {
                    setShowFocusElements(false);
                  }, 50);
                },
                onSuggestSelect: (suggest) => {
                  props.onSuggestSelect && props.onSuggestSelect(suggest);
                  setShowFocusElements(false);
                },
              }
            : null)}
        />
        {Icon && <div className={inputIcon({ iconPosition })}>{Icon}</div>}
      </div>
      {showFocusElements && (
        <div className={`focus-elements ${styles.focusElementsWrapp}`}>
          {focusElements}
        </div>
      )}
    </div>
  );
};
export default GoogleGEOAutocomplete;
